'use client';
import React, { useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

interface ActivityData {
  steps: number | null;
  estimatedBurn: number | null;
  sleepHours: number | null;
  trainingDone: boolean | null;
  stepsTarget: number;
  sleepTarget: number;
}

export default function ActivityMetrics() {
  const [activity, setActivity] = useState<ActivityData | null>(null);
  const [goalsSet, setGoalsSet] = useState(false);

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(async ({ data: { user } }) => {
      if (!user) return;

      // Check if goals are set
      const { data: profile } = await supabase
        .from('forge_user_profiles')
        .select('goals_setup_complete, target_steps, target_sleep_hours')
        .eq('user_id', user.id)
        .single();

      if (!profile?.goals_setup_complete) return;
      setGoalsSet(true);

      const today = new Date().toISOString().split('T')[0];
      const { data: checkin } = await supabase
        .from('forge_daily_checkins')
        .select('steps_count, sleep_hours, training_completed, calories_burned')
        .eq('user_id', user.id)
        .eq('checkin_date', today)
        .single();

      if (checkin) {
        setActivity({
          steps: checkin.steps_count ?? null,
          estimatedBurn: checkin.calories_burned ?? null,
          sleepHours: checkin.sleep_hours ?? null,
          trainingDone: checkin.training_completed ?? null,
          stepsTarget: profile?.target_steps || 10000,
          sleepTarget: profile?.target_sleep_hours || 7.5,
        });
      } else {
        setActivity({
          steps: null,
          estimatedBurn: null,
          sleepHours: null,
          trainingDone: null,
          stepsTarget: profile?.target_steps || 10000,
          sleepTarget: profile?.target_sleep_hours || 7.5,
        });
      }
    });
  }, []);

  if (!goalsSet) {
    return (
      <div>
        <div className="text-forge-label mb-3">Activity &amp; Recovery</div>
        <div className="card-forge rounded-xl p-6 text-center">
          <div className="text-2xl mb-2">🏃</div>
          <div className="text-xs font-bold text-foreground mb-1">No Activity Data Yet</div>
          <div className="text-2xs text-muted-foreground">Complete goal setup to start tracking activity</div>
        </div>
      </div>
    );
  }

  const metrics = [
    {
      label: 'Steps Today',
      value: activity?.steps != null ? activity.steps.toLocaleString() : '—',
      target: activity ? activity.stepsTarget.toLocaleString() : '—',
      pct: activity?.steps != null ? Math.min(100, Math.round((activity.steps / activity.stepsTarget) * 100)) : 0,
      unit: 'steps',
      color: 'var(--primary)',
    },
    {
      label: 'Est. Burn',
      value: activity?.estimatedBurn != null ? activity.estimatedBurn.toString() : '—',
      target: 'From check-in',
      pct: 0,
      unit: 'kcal',
      color: 'var(--primary)',
    },
    {
      label: 'Sleep',
      value: activity?.sleepHours != null ? activity.sleepHours.toString() : '—',
      target: activity ? `${activity.sleepTarget}h target` : '—',
      pct: activity?.sleepHours != null ? Math.min(100, Math.round((activity.sleepHours / activity.sleepTarget) * 100)) : 0,
      unit: 'hrs',
      color: 'var(--accent)',
    },
    {
      label: 'Training',
      value: activity?.trainingDone == null ? '—' : activity.trainingDone ? 'DONE' : 'PENDING',
      target: 'Log in Check-In',
      pct: activity?.trainingDone ? 100 : 0,
      unit: '',
      color: 'var(--primary)',
    },
  ];

  const hasAnyData = activity && (
    activity.steps != null ||
    activity.estimatedBurn != null ||
    activity.sleepHours != null ||
    activity.trainingDone != null
  );

  return (
    <div>
      <div className="text-forge-label mb-3">Activity &amp; Recovery</div>
      {!hasAnyData ? (
        <div className="card-forge rounded-xl p-6 text-center">
          <div className="text-2xl mb-2">🏃</div>
          <div className="text-xs font-bold text-foreground mb-1">No Activity Logged Today</div>
          <div className="text-2xs text-muted-foreground">Complete your daily Check-In to track steps, sleep &amp; training</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {metrics.map((a) => (
            <div key={`activity-${a.label}`} className="card-forge rounded-lg p-4">
              <div className="text-forge-label mb-2">{a.label}</div>
              <div className="font-tabular font-black text-xl text-foreground leading-none mb-1">
                {a.value}
                {a.unit && a.value !== '—' && (
                  <span className="text-xs text-muted-foreground font-medium ml-1">{a.unit}</span>
                )}
              </div>
              {a.value !== '—' && (
                <div className="macro-bar-track mt-2">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${a.pct}%`,
                      backgroundColor:
                        a.pct >= 100 ? 'var(--primary)' : a.pct >= 80 ? 'var(--accent)' : 'var(--destructive)',
                    }}
                  />
                </div>
              )}
              <div className="text-2xs text-muted-foreground mt-1">{a.target}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}